import { HttpClient } from '@angular/common/http';
import { inject, Injectable } from '@angular/core';
import { AbstractDataService } from '@zero/shared/util-abstract-services';
import {
  Announcement,
  Asset,
  BezugsrechtData,
  CorporateActionDirectiveType,
  CorporateActionType,
  Directive,
} from '@zero/shared/util-models';
import { AssetSearchService } from '@zero/shared/util-services';
import { commonRoutes } from 'constants/web-app-constants';
import { environment } from 'environments/environment';
import { BehaviorSubject, firstValueFrom, Subscription } from 'rxjs';

@Injectable({
  providedIn: 'root',
})
export class BezugsrechtService {
  public isin: string;
  public customerId: string;
  readonly #bezugsrechtData = new BehaviorSubject<BezugsrechtData | undefined>(undefined);
  readonly #subscriptions: Subscription[] = [];

  public readonly bezugsrechtData$ = this.#bezugsrechtData.asObservable();

  readonly #dataService = inject(AbstractDataService);
  readonly #assetSearchService = inject(AssetSearchService);
  readonly #http = inject(HttpClient);

  public loadData = async (isin: string): Promise<void> => {
    this.isin = isin;
    this.customerId = this.#dataService.getSelectedCustomerItem()?.custId ?? null;

    const announcement = await this.#getAnnouncement();
    let asset = null;
    if (announcement?.bzrHandelbarBroker) {
      asset = await this.#loadAsset();
    }

    const buyableAmount = this.#calculateBuyableAmount(announcement);
    const remainingSubscriptionRights = this.#calculateRemaining(announcement, buyableAmount);

    this.#bezugsrechtData.next({
      asset,
      announcement,
      subscriptionRatio: this.#calculateRatio(announcement),
      buyableAmount,
      remainingSubscriptionRights,
    } as BezugsrechtData);

    this.#subscribeToCustomerAccount();
  };

  public clear = (): void => {
    this.#bezugsrechtData.next(undefined);
    if (this.#subscriptions?.length) {
      this.#subscriptions.forEach((subscription) => subscription.unsubscribe());
    }
  };

  public async getAnnouncements(customerId: string): Promise<Announcement[]> {
    const url = `${environment.apihost}${environment.corporateActions.ankuendigung}?customerId=${customerId}`;
    return firstValueFrom(this.#http.get<Announcement[]>(url));
  }

  public async loadDirectivePreview(eventId: number, caType: CorporateActionType, amountShares?: number): Promise<void> {
    let caDirectiveType: CorporateActionDirectiveType;
    switch (caType) {
      case CorporateActionType.TEILRECHT:
        caDirectiveType = amountShares ? CorporateActionDirectiveType.teilrechtKauf : CorporateActionDirectiveType.teilrechtVerkauf;
        amountShares = null; //amountShares only used to indicate buy or sell
        break;
      case CorporateActionType.WDIV:
      case CorporateActionType.WDIF:
        //NO PREVIEW FOR WDIV
        return;
      default:
        caDirectiveType = amountShares ? CorporateActionDirectiveType.kapCustomAmount : CorporateActionDirectiveType.kapDefault;
        break;
    }

    const response = await this.#postDirectivePreview(this.customerId, eventId, caType, caDirectiveType, amountShares);

    if (response) {
      this.#bezugsrechtData.next({
        ...this.#bezugsrechtData.value,
        directivePreview: response,
      });
    }
  }

  public async placeDirective(announcement: Announcement, directivePreview: Directive, buyableAmount?: number): Promise<void> {
    if (!announcement) {
      return;
    }
    let amountShares: number;
    let amountBzr: number;
    const { avisTyp: caType, ereignisnummer: eventId } = announcement;

    if (directivePreview) {
      const { bezugAktien, bezugBzr } = directivePreview;
      amountShares = bezugAktien;
      amountBzr = bezugBzr;
    }

    let caDirectiveType: CorporateActionDirectiveType;
    let sellRemaining = true;

    switch (caType) {
      case CorporateActionType.TEILRECHT:
        caDirectiveType = CorporateActionDirectiveType.teilrechtVerkauf;
        amountShares = null;
        if (amountBzr > 0) {
          caDirectiveType = CorporateActionDirectiveType.teilrechtKauf;
          sellRemaining = false;
        }
        break;
      case CorporateActionType.WDIF:
      case CorporateActionType.WDIV:
        caDirectiveType = CorporateActionDirectiveType.wdivStock;
        break;
      default:
        if (buyableAmount && buyableAmount == amountShares) {
          amountShares = null;
          amountBzr = null;
        }

        caDirectiveType = CorporateActionDirectiveType.kapCustomAmount;
        if (!amountShares) {
          caDirectiveType = CorporateActionDirectiveType.kapDefault;
          amountShares = null;
          amountBzr = null;
        }
    }

    const response = await this.#postDirective(this.customerId, eventId, caType, caDirectiveType, amountShares, amountBzr, sellRemaining);

    if (response) {
      this.#bezugsrechtData.next({
        ...this.#bezugsrechtData.value,
        directive: response,
      });
    }
  }

  public redirectInvalidData(caTypes: CorporateActionType[], router, asset: Asset, announcement: Announcement): boolean {
    if (!announcement) {
      router.navigate([commonRoutes.postlogin.member_home]);
      return true;
    }

    if (!caTypes.includes(announcement.avisTyp)) {
      try {
        const commonRouteByType = this.#getRouteByType(announcement.avisTyp);
        router.navigate([`${commonRouteByType}/${announcement.bzrIsin}`]);
        return true;
      } catch (e) {
        router.navigate([commonRoutes.postlogin.member_home]);
        return true;
      }
    }
    return false;
  }

  #getRouteByType(avisTyp: CorporateActionType): string {
    switch (avisTyp) {
      case CorporateActionType.KAP:
        return commonRoutes.postlogin.bezugsrecht;
      case CorporateActionType.TEILRECHT:
        return commonRoutes.postlogin.teilrecht;
      case CorporateActionType.WDIV:
      case CorporateActionType.WDIF:
        return commonRoutes.postlogin.wahldividende;
      default:
        throw new Error(`missing route mapping for '${CorporateActionType[avisTyp]}'`);
    }
  }

  async #getAnnouncement(): Promise<Announcement> {
    const announcements = await this.getAnnouncements(this.customerId);
    return announcements ? announcements.find(({ bzrIsin }) => bzrIsin === this.isin) : null;
  }

  #loadAsset(): Promise<Asset> {
    return firstValueFrom(this.#assetSearchService.loadAssetByIsin(this.isin));
  }

  #calculateRatio(announcement: Announcement) {
    if (!announcement) {
      return 0;
    }
    return announcement.bezugVerhaeltnisNenner / announcement.bezugVerhaeltnisZaehler;
  }

  #calculateRemaining(announcement: Announcement, buyableAmount: number) {
    if (!announcement) {
      return 0;
    }
    return announcement.bzrVerfuegbar - (buyableAmount / announcement.bezugVerhaeltnisZaehler) * announcement.bezugVerhaeltnisNenner;
  }

  #calculateBuyableAmount(announcement: Announcement) {
    if (!announcement) {
      return 0;
    }
    return Math.floor(announcement.bzrVerfuegbar / announcement.bezugVerhaeltnisNenner) * announcement.bezugVerhaeltnisZaehler;
  }

  #subscribeToCustomerAccount(): void {
    const subscription = this.#dataService.loadBPDetails$(this.isin).subscribe((bpDetails) => {
      if (!bpDetails) {
        return;
      }

      this.#bezugsrechtData.next({
        ...this.#bezugsrechtData.value,
        buyingPower: bpDetails.buyingPower,
      });
    });

    this.#subscriptions.push(subscription);
  }

  async #postDirectivePreview(
    customerId: string,
    eventId: number,
    caType: CorporateActionType,
    caDirectiveType: CorporateActionDirectiveType,
    amountShares?: number,
  ): Promise<Directive> {
    if (!customerId || !eventId || !caDirectiveType) {
      return null;
    }

    const url = `${environment.apihost}${environment.corporateActions.weisungVorschau}`;
    const body = {
      customerId: customerId,
      ereignisnummer: eventId,
      weisungTyp: caDirectiveType,
      bezugAktien: amountShares,
      avisTyp: caType,
    };

    return firstValueFrom(this.#http.post<Directive>(url, body));
  }

  async #postDirective(
    customerId: string,
    eventId: number,
    caType: CorporateActionType,
    caDirectiveType: CorporateActionDirectiveType,
    amountShares?: number,
    amountBzr?: number,
    sellRemaining = true,
  ): Promise<Directive> {
    if (!customerId || !eventId || !caDirectiveType) {
      return null;
    }

    const url = `${environment.apihost}${environment.corporateActions.weisungErteilen}`;
    const body = {
      customerId: customerId,
      ereignisnummer: eventId,
      weisungTyp: caDirectiveType,
      verkaufRestlicheBzr: sellRemaining,
      bezugAktien: amountShares,
      bezugBzr: amountBzr,
      avisTyp: caType,
    };

    return firstValueFrom(this.#http.post<Directive>(url, body));
  }
}
